
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify"; // Import toast library
import { Plus } from "assets/icons";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell } from "@heroui/react";
import { Money } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import AddProductModal from "./components/addProductModal";
import { CreateYallyProductModal } from "./components/createYallyProductModal";
import { YallyCompany } from "shared/types/yally-company";

export interface CompanyProductsProps {
    companies: YallyCompany[];
}

export const CompanyProducts = ({ companies }: CompanyProductsProps) => {
    const { session } = useSession();
    const [productTemplates, setProductTemplates] = useState<any[]>([]);
    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);

    const handleAddProductModalOpen = () => setIsAddProductModalOpen(true);
    const handleAddProductModalClose = () => setIsAddProductModalOpen(false);

    const fetchProductTemplates = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/companies/product-variants`,
                { method: "GET" },
                session,
            );
            const data = await response.json();
            const sorted = (Array.isArray(data) ? data : []).slice().sort((a: any, b: any) => {
                const an = (a?.company?.name || '').toString();
                const bn = (b?.company?.name || '').toString();
                const byCompany = an.localeCompare(bn, 'nl', { sensitivity: 'base' });
                if (byCompany !== 0) return byCompany;
                const ap = (a?.name || '').toString();
                const bp = (b?.name || '').toString();
                return ap.localeCompare(bp, 'nl', { sensitivity: 'base' });
            });
            setProductTemplates(sorted);
        } catch (error) {
            console.error("Error fetching product templates:", error);
            toast.error("Failed to load product templates");
        }
    };

    useEffect(() => {
        fetchProductTemplates();
    }, []);

    return (
        < div className="flex flex-col gap-2.5" >
            <header className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-1.5">
                    <Money className="h-[18px] w-[18px]" />
                    <p className="text-lg">Yally producten per vennootschap</p>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <ButtonNew
                        variant="secondaryMoss"
                        onClick={handleAddProductModalOpen}
                        icon={<Plus />}
                        label="Importeer productvariant uit Odoo"
                    />
                    <ButtonNew
                        variant="primaryMoss"
                        onClick={() => setIsCreateProductModalOpen(true)}
                        icon={<Plus />}
                        label="Nieuw product voor alle vennootschappen"
                    />
                </div>
            </header>
            <div className="rounded-lg border border-moss-primary/20 bg-moss-primary/5 p-3 text-sm text-neutral-primary">
                Dit zijn de producten zoals ze per vennootschap in Odoo bestaan. Ze komen uit de{" "}
                <span className="font-medium">productsjablonen</span> (tabblad Productsjablonen):
                één sjabloon = één product per vennootschap. Maak je hier een nieuw product, dan
                wordt het sjabloon meteen mee aangemaakt. Importeer je er één uit Odoo, dan wordt
                het sjabloon aangevuld als het nog niet bestond.
            </div>
            <Table
                classNames={{
                    wrapper: "shadow-none rounded-none p-0",
                    thead: "h-fit border-b-2 border-neutral-primary/40",
                    th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                    tbody: "border-b divide-y divide-neutral-primary/15",
                    tr: "divide-x divide-neutral-primary/15",
                    td: "py-2.5",
                }}
            >
                <TableHeader>
                    <TableColumn>Bedrijf</TableColumn>
                    <TableColumn>Product naam</TableColumn>
                    <TableColumn>Odoo Product variant ID</TableColumn>
                    <TableColumn>Grootboekrekening</TableColumn>
                </TableHeader>
                <TableBody>
                    {productTemplates && productTemplates.map((template) => (
                        <TableRow key={template.id}>
                            <TableCell>{template.company && template.company.name}</TableCell>
                            <TableCell>{template.name}</TableCell>
                            <TableCell>{template.odooProductVariantId}</TableCell>
                            <TableCell>{template.ledger}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <AddProductModal
                companies={companies}
                isOpen={isAddProductModalOpen}
                onClose={handleAddProductModalClose}
                fetchProductTemplates={fetchProductTemplates}
            />
            <CreateYallyProductModal
                isOpen={isCreateProductModalOpen}
                onClose={() => setIsCreateProductModalOpen(false)}
                companies={companies}
                onCreated={fetchProductTemplates}
            />
        </div >
    )
};